import {
  Alert,
  Button,
  Card,
  DatePicker,
  Empty,
  Input,
  Select,
  Space,
  Table,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import {
  type AcquisitionBinding,
  type AcquisitionChannel,
  type AcquisitionFilter,
  acquisitionApi,
} from '@/services/acquisitionApi';
import { AcquisitionStore } from '@/stores/acquisitionStore';

const formatTime = (value: string) => new Date(value).toLocaleString();
export const AcquisitionAttributionsPage = observer(() => {
  const [store] = useState(() => new AcquisitionStore(acquisitionApi));
  const [channel, setChannel] = useState('');
  const [userId, setUserId] = useState('');
  const [period, setPeriod] = useState<'range' | 'all'>('range');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(29, 'day').startOf('day'),
    dayjs().add(1, 'day').startOf('day'),
  ]);
  const [validation, setValidation] = useState('');
  const [initialFilter] = useState<AcquisitionFilter>(() => ({
    startAt: range[0].toISOString(),
    endAt: range[1].toISOString(),
  }));
  const filters = (): AcquisitionFilter => ({
    trafficContentId: channel || undefined,
    ...(period === 'range'
      ? { startAt: range[0].toISOString(), endAt: range[1].toISOString() }
      : {}),
  });
  useEffect(() => {
    void store.loadSummary(initialFilter);
    return () => store.clear();
  }, [store, initialFilter]);
  const search = () => {
    if (channel && !/^[a-z0-9_-]{1,64}$/.test(channel)) {
      setValidation('渠道码为 1–64 位小写字母、数字、下划线或连字符。');
      return;
    }
    if (
      period === 'range' &&
      (range[1].valueOf() <= range[0].valueOf() ||
        range[1].diff(range[0], 'millisecond') > 93 * 86400000)
    ) {
      setValidation('请选择不超过 93 天的有效时间范围。');
      return;
    }
    setValidation('');
    void store.loadSummary(filters());
  };
  const bindingColumns = [
    { title: '用户 ID', dataIndex: 'userId', key: 'userId' },
    { title: '渠道码', dataIndex: 'trafficContentId', key: 'trafficContentId' },
    {
      title: '绑定时间',
      dataIndex: 'boundAt',
      key: 'boundAt',
      render: formatTime,
    },
  ];
  return (
    <div className="min-w-0 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">渠道归因</h1>
        <p className="mt-2 text-muted-foreground">
          查询推广渠道的首次成功绑定。已有账号也计入绑定用户数；时间为绑定时间。
        </p>
      </div>
      <Card title="渠道汇总">
        <Space wrap className="mb-4">
          <Input
            aria-label="渠道码"
            placeholder="全部渠道，或输入精确渠道码"
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
          />
          <Select
            aria-label="统计范围"
            value={period}
            onChange={setPeriod}
            options={[
              { value: 'range', label: '时间范围' },
              { value: 'all', label: '累计绑定' },
            ]}
          />
          {period === 'range' && (
            <DatePicker.RangePicker
              aria-label="绑定时间范围"
              showTime
              value={range}
              allowClear={false}
              onChange={(value) => {
                if (value?.[0] && value[1]) setRange([value[0], value[1]]);
              }}
            />
          )}
          <Button
            type="primary"
            loading={store.summaryLoading}
            disabled={store.denied}
            onClick={search}
          >
            查询 / 刷新
          </Button>
        </Space>
        <p className="mb-4 text-sm text-muted-foreground">
          显示时区：{Intl.DateTimeFormat().resolvedOptions().timeZone}
          ；时间范围包含开始、不包含结束。未配置过的合法渠道码也可查询。
        </p>
        {(validation || store.error) && (
          <Alert
            type="error"
            showIcon
            title={validation || store.error}
            className="mb-4"
          />
        )}
        <p className="mb-3">
          绑定用户数：<strong>{store.total}</strong>
          {store.snapshotAt && (
            <span className="ml-4 text-sm text-muted-foreground">
              查询截止：{formatTime(store.snapshotAt)}
            </span>
          )}
        </p>
        <Table<AcquisitionChannel>
          size="small"
          rowKey="trafficContentId"
          loading={store.summaryLoading}
          dataSource={store.channels}
          scroll={{ x: 500 }}
          pagination={false}
          columns={[
            { title: '渠道码', dataIndex: 'trafficContentId' },
            { title: '绑定用户数', dataIndex: 'boundUsers' },
            {
              title: '操作',
              key: 'action',
              render: (_, row) => (
                <Button
                  type="link"
                  onClick={() => void store.loadBindings(row.trafficContentId)}
                >
                  查看明细
                </Button>
              ),
            },
          ]}
        />
        {store.summaryNext && (
          <Button
            className="mt-4"
            loading={store.summaryLoading}
            onClick={() => void store.loadSummary()}
          >
            加载更多渠道
          </Button>
        )}
      </Card>
      <Card
        title={
          store.selectedChannel
            ? `绑定明细 · ${store.selectedChannel}`
            : '绑定明细'
        }
      >
        {store.selectedChannel ? (
          <>
            <Table<AcquisitionBinding>
              size="small"
              rowKey="userId"
              loading={store.bindingsLoading}
              dataSource={store.bindings}
              columns={bindingColumns}
              scroll={{ x: 500 }}
              pagination={false}
            />
            <Space className="mt-4">
              <Button
                loading={store.bindingsLoading}
                disabled={store.denied}
                onClick={() => void store.loadBindings(store.selectedChannel)}
              >
                刷新明细
              </Button>
              {store.bindingsNext && (
                <Button
                  loading={store.bindingsLoading}
                  onClick={() => void store.loadBindings()}
                >
                  加载更多绑定
                </Button>
              )}
            </Space>
          </>
        ) : (
          <Empty description="从渠道汇总中选择一个渠道查看绑定用户。" />
        )}
      </Card>
      <Card title="用户来源查询">
        <Space wrap className="mb-4">
          <Input
            aria-label="用户 ID"
            placeholder="输入精确用户 ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          />
          <Button
            loading={store.userLoading}
            disabled={store.denied || !/^[A-Za-z0-9_.@:-]{1,191}$/.test(userId)}
            onClick={() => void store.lookupUser(userId)}
          >
            查询来源
          </Button>
        </Space>
        {store.user &&
          (store.user.bound && store.user.binding ? (
            <Table<AcquisitionBinding>
              size="small"
              rowKey="userId"
              scroll={{ x: 500 }}
              pagination={false}
              columns={bindingColumns}
              dataSource={[store.user.binding]}
            />
          ) : (
            <Alert
              type="info"
              title={`${store.queriedUserId} 暂无渠道绑定记录；此结果不表示账号不存在。`}
            />
          ))}
      </Card>
    </div>
  );
});
